import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CoArchitectLogo } from '../components/Icons';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';

const TESTIMONIALS = [
  { name: 'Meera', region: 'IN', quote: "Uploaded a tile photo and saw it in my actual living room in seconds. Convinced my whole family in one sitting.", featured: true },
  { name: 'Jonas', region: 'DE', quote: "Swapped the sofa in one click and could finally see if the color actually worked with our rug.", featured: false },
  { name: 'Priya', region: 'US', quote: "Tried five styles on the same room before picking one. Would've taken a decorator a week.", featured: false },
  { name: 'Liam', region: 'GB', quote: "Used it to show my client three finish options before we bought anything. Saved us a return.", featured: false },
  { name: 'Ana', region: 'BR', quote: "The chat follow-ups are the best part — 'make it warmer' and it just gets it.", featured: false },
] as const;

/* ── Flow arrows ─────────────────────────────────────────────── */
function FlowArrowH({ flip = false }: { flip?: boolean }) {
  return (
    <div className="flow-arrow-h" style={{ transform: flip ? 'scaleX(-1)' : 'none' }}>
      <svg width="24" height="14" viewBox="0 0 24 14">
        <path d="M0 7 L17 7" stroke="var(--accent)" strokeWidth="1.8" fill="none" strokeDasharray="3 2"/>
        <polyline points="13,3 19,7 13,11" stroke="var(--accent)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

function FlowArrowDown({ flip = false }: { flip?: boolean }) {
  return (
    <div className={`flow-arrow-down${flip ? ' flip' : ''}`} style={{ flex: 1 }}>
      <svg width="14" height="22" viewBox="0 0 14 22">
        <path d="M7 0 L7 16" stroke="var(--accent)" strokeWidth="1.8" fill="none" strokeDasharray="3 2"/>
        <polyline points="3,13 7,19 11,13" stroke="var(--accent)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/* ── Step card ───────────────────────────────────────────────── */
function StepCard({ icon, label, body, highlight = false }: {
  icon: React.ReactNode; label: string; body: string; highlight?: boolean;
}) {
  return (
    <div className={`step-card${highlight ? ' step-highlight' : ''}`}>
      <div className="step-icon">{icon}</div>
      <p className="step-label">{label}</p>
      <p className="step-body">{body}</p>
    </div>
  );
}

/* ── Tool card ───────────────────────────────────────────────── */
function ToolCard({ icon, title, body, onClick }: {
  icon: React.ReactNode; title: string; body: string; onClick: () => void;
}) {
  return (
    <button className="tool-card" onClick={onClick}>
      <div className="tool-card-icon">{icon}</div>
      <p className="tool-card-title">{title}</p>
      <p className="tool-card-body">{body}</p>
    </button>
  );
}

/* ── Landing Page ────────────────────────────────────────────── */
function Landing({ isAbout = false }: { isAbout?: boolean }) {
  const navigate = useNavigate();
  const { storeTokens } = useAuth();

  const startGuestFlow = async () => {
    try {
      const res = await axios.post(`${API}/guest/session`);
      await storeTokens(res.data.tokens.accessToken, res.data.tokens.refreshToken);
      navigate('/new-project', { state: { prefillName: 'Default Project' } });
    } catch (err: any) {
      console.error('Failed to start guest session', err);
      // A guest has no account to log into — /login is a dead end here (worst case: the
      // 409 "already used your free preview today" collision, expected in CGNAT markets).
      // Send them to /signup instead, carrying the server's message along if there is one.
      const message: string | undefined = err?.response?.data?.message;
      navigate('/signup', message ? { state: { guestError: message } } : undefined);
    }
  };

  const [sliderX, setSliderX] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const updatePos = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setSliderX(pct);
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => { if (dragging.current) updatePos(e.clientX); };
    const onMouseUp = () => { dragging.current = false; };
    const onTouchMove = (e: TouchEvent) => { if (dragging.current) updatePos(e.touches[0].clientX); };
    const onTouchEnd = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [updatePos]);

  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-root">

      {/* Top bar */}
      <div className="landing-topbar">
        <div className="landing-logo">
          <div className="landing-logo-mark">
            <CoArchitectLogo size={34} />
          </div>
          <span className="landing-logo-text">Co-Architect</span>
        </div>
        {!isAbout && (
          <div className="landing-topbar-actions">
            <button className="landing-pricing-btn" onClick={() => navigate('/pricing')}>Pricing</button>
            <button className="landing-signin-btn" onClick={() => navigate('/login')}>Sign In</button>
          </div>
        )}
      </div>

      {/* Scrollable body */}
      <div className="landing-scroll">

        {/* Hero + slider (split row on desktop) */}
        <div className="landing-hero-row">
        <div className="landing-hero">
          <p className="landing-eyebrow">Interior Design AI</p>
          <h1 className="landing-headline">
            Reimagine your space<br /><em>with AI.</em>
          </h1>
          <p className="landing-subhead">
            Snap a tile, fabric, or finish. Co-Architect places it into any room scene — photorealistically. No studio, no staging.
          </p>
          {!isAbout && (
            <div className="landing-cta-row">
              <button className="landing-cta-main" onClick={startGuestFlow}>Visualize My Space →</button>
              <button className="landing-cta-sec" onClick={() => navigate('/register')}>Sign Up Free</button>
            </div>
          )}
        </div>

        {/* Before / After slider */}
        <div className="landing-slider-wrap">
          <div
            ref={containerRef}
            className="landing-slider"
            onMouseDown={() => { dragging.current = true; }}
            onTouchStart={() => { dragging.current = true; }}
          >
            <img src="/images/after.png" className="slider-img" alt="After AI redesign" draggable={false} />
            <img
              src="/images/before.png"
              className="slider-img"
              style={{ clipPath: `inset(0 ${100 - sliderX}% 0 0)` }}
              alt="Before"
              draggable={false}
            />
            <div className="slider-divider" style={{ left: `${sliderX}%` }} />
            <div className="slider-handle" style={{ left: `${sliderX}%` }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <polyline points="7,4 3,10 7,16" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="13,4 17,10 13,16" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="slider-badge slider-badge-before">BEFORE</span>
            <span className="slider-badge slider-badge-after">AFTER ✦ AI</span>
            {sliderX === 50 && (
              <div className="slider-hint">← drag to compare →</div>
            )}
          </div>
          <p className="slider-caption">Drag to compare · see your materials placed</p>
        </div>
        </div>

        {/* How it works */}
        <div className="landing-how">
          <h2 className="landing-how-title">How it works</h2>
          <p className="landing-how-sub">From material to photorealistic room in 4 steps</p>

          <div className="landing-how-rows-mobile">
          <div className="how-row">
            <StepCard
              label="Snap Your Material"
              body="Photo or describe any tile, fabric, or finish"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              }
            />
            <FlowArrowH />
            <StepCard
              label="Pick a Space"
              body="Choose room type or upload your own photo"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              }
            />
          </div>

          <div className="how-arrows-mid">
            <div style={{ flex: 1 }} />
            <FlowArrowDown />
            <div style={{ width: 24 }} />
            <FlowArrowDown flip />
            <div style={{ flex: 1 }} />
          </div>

          <div className="how-row">
            <StepCard
              label="Save & Share"
              body="Download or share directly with your client"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              }
            />
            <FlowArrowH flip />
            <StepCard
              highlight
              label="AI Renders It"
              body="Photorealistic placement in seconds"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              }
            />
          </div>
          </div>

          <div className="landing-how-rows-desktop">
            <StepCard
              label="Snap Your Material"
              body="Photo or describe any tile, fabric, or finish"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              }
            />
            <FlowArrowH />
            <StepCard
              label="Pick a Space"
              body="Choose room type or upload your own photo"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              }
            />
            <FlowArrowH />
            <StepCard
              highlight
              label="AI Renders It"
              body="Photorealistic placement in seconds"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              }
            />
            <FlowArrowH />
            <StepCard
              label="Save & Share"
              body="Download or share directly with your client"
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              }
            />
          </div>
        </div>

        {/* Explore AI Tools */}
        <div className="landing-tools scroll-reveal" data-reveal>
          <h2 className="landing-how-title">Explore AI Tools</h2>
          <p className="landing-how-sub">Everything you need to visualize a space</p>
          <div className="landing-tools-grid">
            <ToolCard
              title="Material Placement"
              body="Place any tile, fabric, or finish into your room, photorealistically."
              onClick={startGuestFlow}
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="13" height="7" rx="2"/>
                  <rect x="6" y="10" width="4" height="10" rx="1"/>
                  <path d="M18 5h4v5h-4z"/>
                </svg>
              }
            />
            <ToolCard
              title="Object Swap"
              body="Click any object in your photo and swap it instantly."
              onClick={startGuestFlow}
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="17 2 21 6 17 10"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 22 3 18 7 14"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              }
            />
            <ToolCard
              title="Style Explorer"
              body="Browse curated room styles for inspiration."
              onClick={startGuestFlow}
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
                </svg>
              }
            />
            <ToolCard
              title="AI Design Chat"
              body="Refine your render with follow-up instructions."
              onClick={startGuestFlow}
              icon={
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
              }
            />
          </div>
        </div>

        {/* Testimonials */}
        <div className="landing-testimonials scroll-reveal" data-reveal>
          <p className="landing-eyebrow">1,000+ users</p>
          <h2 className="landing-how-title">Loved by early users</h2>
          <div className="testimonial-grid">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className={`testimonial-card${t.featured ? ' testimonial-featured' : ''}`}>
                <div className="testimonial-stars">★★★★★</div>
                <p className="testimonial-quote">"{t.quote}"</p>
                <p className="testimonial-author">{t.name} <span className="testimonial-region">{t.region}</span></p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="landing-bottom-cta">
          <div className="landing-cta-card">
            <p className="landing-cta-card-title">Ready to visualise?</p>
            <p className="landing-cta-card-sub">Join designers already using Co-Architect</p>
            <button className="landing-cta-full" onClick={isAbout ? () => navigate('/new-project') : startGuestFlow}>
              Let's Visualize Your Space →
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="landing-footer">
          <a href="https://www.instagram.com/coarchitect.app" target="_blank" rel="noopener noreferrer" className="auth-social-row">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
            </svg>
            @coarchitect.app
          </a>
          <div className="auth-legal-row">
            <Link to="/privacy" className="auth-legal-link">Privacy Policy</Link>
            <span className="auth-legal-sep" />
            <Link to="/terms" className="auth-legal-link">Terms of Service</Link>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Landing;
